import { validate as isValidUuid, v4 } from 'uuid'
import { builderIdStart } from './agent-builder-route'

/**
 * The flow builder uses the browser URL path segment (`/agent/builder/:id`) as the PHLO id
 * and sends it verbatim to the backend on save/publish. A hand-edited URL (e.g. `<uuid>ss`,
 * or `amankhemka`) would otherwise be persisted as the PHLO uuid and break replication into
 * ClickHouse's strictly-typed UUID column — pg2ch retries the rejected insert indefinitely
 * (SER-6234). Regenerate a fresh valid UUID when the candidate id is present but not a valid
 * UUID. An empty/undefined candidate is returned unchanged so the backend mints the id
 * (`POST /phlo` with no id segment). `onSuccess` then adopts the resulting id into the URL.
 */
export const resolvePhloUpsertId = (candidateId: string | undefined): string | undefined =>
  candidateId && !isValidUuid(candidateId) ? v4() : candidateId

/**
 * Rewrite the `:id` segment of a builder pathname (`/agent/builder/:id[/subtab]`) to `newId`,
 * preserving any sub-path/tab. Positional (finds the segment after the builder base)
 * rather than matching the old id — `useParams().id` is percent-decoded while `location.pathname`
 * stays raw/encoded, so a match on the decoded id would miss an encoded segment. Used to adopt
 * the regenerated/backend id into the URL after save without a remount, so the canvas is preserved.
 */
export const swapBuilderPathId = (pathname: string, newId: string): string => {
  const idStart = builderIdStart(pathname)
  if (idStart === -1) return pathname
  const nextSlash = pathname.indexOf('/', idStart)
  const suffix = nextSlash === -1 ? '' : pathname.slice(nextSlash)
  return pathname.slice(0, idStart) + newId + suffix
}
